import { useEffect, useState } from "react";
import { GlassPanel } from "@/components/ui/glass-panel";
import { Sunrise, Sunset, Stars, Moon } from "lucide-react";
import { useLocation } from "@/components/location-provider";
import { twilightTimes } from "@/lib/astronomy";
import { cn } from "@/lib/utils";

function fmt(ts: number | null): string {
  if (ts == null) return "—";
  return new Date(ts * 1000).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

export function TwilightBar() {
  const [now, setNow] = useState(() => new Date());
  const { current: location } = useLocation();

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(interval);
  }, []);

  const tw = twilightTimes(now, location.lat, location.lon);

  // Build segments across the day from local midnight to next local midnight.
  // For visualization we take a 24h window centered on local noon.
  const offsetSec = location.lon * 240; // approx local timezone offset (4 min/deg)
  const localMidnight =
    Math.floor((now.getTime() / 1000 + offsetSec) / 86400) * 86400 - offsetSec;
  const dayEndSec = localMidnight + 86400;

  // Phase boundaries in seconds from start of window.
  // Order through the day: night → astro dawn → nautical → civil → day → civil dusk → nautical → astro → night
  const events: { ts: number | null; label: string }[] = [
    { ts: tw.astroDawn, label: "Astronomical dawn" },
    { ts: tw.nauticalDawn, label: "Nautical dawn" },
    { ts: tw.civilDawn, label: "Civil dawn" },
    { ts: tw.sunrise, label: "Sunrise" },
    { ts: tw.sunset, label: "Sunset" },
    { ts: tw.civilDusk, label: "Civil dusk" },
    { ts: tw.nauticalDusk, label: "Nautical dusk" },
    { ts: tw.astroDusk, label: "Astronomical dusk" },
  ];

  const totalSec = 86400;
  function pct(ts: number): number {
    return Math.max(0, Math.min(100, ((ts - localMidnight) / totalSec) * 100));
  }

  // Determine current phase
  const currentSec = now.getTime() / 1000;
  let currentPhase: { name: string; tone: string } = {
    name: "Night",
    tone: "bg-slate-800",
  };
  if (tw.alwaysDay) {
    currentPhase = { name: "Continuous daylight", tone: "bg-amber-500/40" };
  } else if (tw.alwaysNight) {
    currentPhase = { name: "Polar night", tone: "bg-slate-900" };
  } else {
    if (tw.sunrise && tw.sunset && currentSec >= tw.sunrise && currentSec < tw.sunset) {
      currentPhase = { name: "Day", tone: "bg-amber-400" };
    } else if (
      (tw.civilDawn && currentSec >= tw.civilDawn && tw.sunrise && currentSec < tw.sunrise) ||
      (tw.sunset && currentSec >= tw.sunset && tw.civilDusk && currentSec < tw.civilDusk)
    ) {
      currentPhase = { name: "Civil twilight", tone: "bg-orange-400" };
    } else if (
      (tw.nauticalDawn && currentSec >= tw.nauticalDawn && tw.civilDawn && currentSec < tw.civilDawn) ||
      (tw.civilDusk && currentSec >= tw.civilDusk && tw.nauticalDusk && currentSec < tw.nauticalDusk)
    ) {
      currentPhase = { name: "Nautical twilight", tone: "bg-violet-400" };
    } else if (
      (tw.astroDawn && currentSec >= tw.astroDawn && tw.nauticalDawn && currentSec < tw.nauticalDawn) ||
      (tw.nauticalDusk && currentSec >= tw.nauticalDusk && tw.astroDusk && currentSec < tw.astroDusk)
    ) {
      currentPhase = { name: "Astronomical twilight", tone: "bg-indigo-500" };
    } else {
      currentPhase = { name: "Astronomical night", tone: "bg-slate-900" };
    }
  }

  // Build day-night gradient bar with twilight bands
  // We build five bands: night (start) | astro→nautical→civil dawn | day | civil→nautical→astro dusk | night (end)
  const bands: { from: number; to: number; color: string; label: string }[] = [];
  function pushBand(fromTs: number, toTs: number, color: string, label: string) {
    if (toTs <= fromTs) return;
    bands.push({
      from: pct(fromTs),
      to: pct(toTs),
      color,
      label,
    });
  }

  if (tw.astroDawn) pushBand(localMidnight, tw.astroDawn, "bg-slate-900", "Night");
  if (tw.astroDawn && tw.nauticalDawn) pushBand(tw.astroDawn, tw.nauticalDawn, "bg-indigo-700/80", "Astronomical");
  if (tw.nauticalDawn && tw.civilDawn) pushBand(tw.nauticalDawn, tw.civilDawn, "bg-violet-500/80", "Nautical");
  if (tw.civilDawn && tw.sunrise) pushBand(tw.civilDawn, tw.sunrise, "bg-orange-400/80", "Civil");
  if (tw.sunrise && tw.sunset) pushBand(tw.sunrise, tw.sunset, "bg-amber-300", "Day");
  if (tw.sunset && tw.civilDusk) pushBand(tw.sunset, tw.civilDusk, "bg-orange-400/80", "Civil");
  if (tw.civilDusk && tw.nauticalDusk) pushBand(tw.civilDusk, tw.nauticalDusk, "bg-violet-500/80", "Nautical");
  if (tw.nauticalDusk && tw.astroDusk) pushBand(tw.nauticalDusk, tw.astroDusk, "bg-indigo-700/80", "Astronomical");
  if (tw.astroDusk) pushBand(tw.astroDusk, dayEndSec, "bg-slate-900", "Night");
  if (bands.length === 0) {
    bands.push({ from: 0, to: 100, color: tw.alwaysDay ? "bg-amber-300" : "bg-slate-900", label: "—" });
  }

  const nowPct = pct(currentSec);

  return (
    <GlassPanel intensity="low" className="p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="text-sm font-bold tracking-tight">Twilight & Daylight</h3>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Solar altitude through the day · 0° / -6° / -12° / -18°
          </p>
        </div>
        <span
          className={cn(
            "rounded-full text-[10px] font-bold uppercase tracking-wider px-2.5 py-1",
            "bg-background/60 border border-border/40",
          )}
        >
          Now: {currentPhase.name}
        </span>
      </div>

      <div className="relative h-9 rounded-lg overflow-hidden border border-border/40 bg-slate-950 mb-2">
        {bands.map((b, i) => (
          <div
            key={i}
            className={cn("absolute top-0 bottom-0", b.color)}
            style={{ left: `${b.from}%`, width: `${b.to - b.from}%` }}
            title={b.label}
          />
        ))}
        {/* Now marker */}
        <div
          className="absolute top-0 bottom-0 w-px bg-white shadow-[0_0_8px_rgba(255,255,255,0.9)]"
          style={{ left: `${nowPct}%` }}
        />
        <div
          className="absolute -top-1 h-2 w-2 rounded-full bg-white shadow-[0_0_8px_rgba(255,255,255,0.9)]"
          style={{ left: `calc(${nowPct}% - 4px)` }}
        />
      </div>

      {/* Time markers under the bar */}
      <div className="relative h-3 mb-3">
        {[0, 6, 12, 18, 24].map((h) => (
          <span
            key={h}
            className="absolute text-[9px] text-muted-foreground tabular-nums -translate-x-1/2"
            style={{ left: `${(h / 24) * 100}%` }}
          >
            {h.toString().padStart(2, "0")}:00
          </span>
        ))}
      </div>

      {/* Phase event chips */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
        <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 px-2.5 py-1.5">
          <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold inline-flex items-center gap-1">
            <Sunrise className="h-2.5 w-2.5 text-amber-400" /> Sunrise
          </p>
          <p className="text-xs font-bold tabular-nums">{fmt(tw.sunrise)}</p>
        </div>
        <div className="rounded-lg bg-orange-500/10 border border-orange-500/20 px-2.5 py-1.5">
          <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold inline-flex items-center gap-1">
            <Sunset className="h-2.5 w-2.5 text-orange-400" /> Sunset
          </p>
          <p className="text-xs font-bold tabular-nums">{fmt(tw.sunset)}</p>
        </div>
        <div className="rounded-lg bg-violet-500/10 border border-violet-500/20 px-2.5 py-1.5">
          <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold inline-flex items-center gap-1">
            <Stars className="h-2.5 w-2.5 text-violet-400" /> Astro start
          </p>
          <p className="text-xs font-bold tabular-nums">{fmt(tw.astroDusk)}</p>
        </div>
        <div className="rounded-lg bg-indigo-500/10 border border-indigo-500/20 px-2.5 py-1.5">
          <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold inline-flex items-center gap-1">
            <Moon className="h-2.5 w-2.5 text-indigo-400" /> Astro end
          </p>
          <p className="text-xs font-bold tabular-nums">{fmt(tw.astroDawn)}</p>
        </div>
      </div>
    </GlassPanel>
  );
}
